import {
	type ConcernState,
	concernNoun,
	deriveUploadProgress,
	FileThumbnail,
	failedConcerns,
	type ThumbnailSize,
	useFilePreview,
} from '@atlas/ui-components'
import { fileTypeIcon, fileTypeToken, typeMarker } from '@atlas/ui-domain'
import { Button, cn, ProgressBar } from '@atlas/ui-primitives'
import { AlertTriangle, Download, GripVertical, Trash2, X } from 'lucide-react'

/**
 * The quiet half of the concern model: nothing at all while post-upload work
 * succeeds, a warning only when it does not.
 *
 * Concerns are our plumbing — the user's file is stored either way. Reporting
 * each one's progress made the UI withhold success for seconds over work nobody
 * asked about; reporting only failures keeps the one case that needs an action.
 */
function ConcernWarning({
	concerns,
	onRetryConcern,
}: {
	concerns: Record<string, ConcernState>
	onRetryConcern?: (concernId: string) => void
}) {
	const failures = failedConcerns(concerns)
	if (failures.length === 0) return null
	return (
		<span className="flex items-center gap-1 text-ds-xs text-ds-danger">
			<AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
			{failures.map(concernNoun).join(', ')} failed
			{onRetryConcern && (
				<Button variant="muted" onPress={() => onRetryConcern(failures[0])}>
					Retry
				</Button>
			)}
		</span>
	)
}

/**
 * The card's thumbnail, clickable when there is a real image behind it.
 *
 * Only a row with an actual raster becomes a button: making every thumbnail
 * focusable would put a dead tab stop on each PSD and PDF, whose "thumbnail" is
 * a format glyph with nothing to enlarge.
 */
function CardThumbnail({
	name,
	contentType,
	previewSrc,
	thumbnailSize,
	onPreview,
	onOpen,
}: {
	name: string
	contentType?: string
	previewSrc?: string
	thumbnailSize: ThumbnailSize
	onPreview?: (src: string) => void
	onOpen?: () => void
}) {
	const thumbnail = (
		<FileThumbnail
			name={name}
			thumbnailUrl={previewSrc}
			size={thumbnailSize}
			// A document or PSD will never get a generated thumbnail, so the
			// placeholder names the format instead of showing a blank page glyph.
			icon={fileTypeIcon(fileTypeToken(contentType, name))}
			marker={typeMarker(contentType ?? '')}
		/>
	)
	if (onOpen) {
		return (
			<button
				type="button"
				aria-label={`Open ${name}`}
				onClick={onOpen}
				className="flex-none rounded-ds-control transition-opacity hover:opacity-80"
			>
				{thumbnail}
			</button>
		)
	}
	if (!previewSrc || !onPreview) return thumbnail
	return (
		<button
			type="button"
			aria-label={`Preview ${name}`}
			onClick={() => onPreview(previewSrc)}
			className="flex-none rounded-ds-control transition-opacity hover:opacity-80"
		>
			{thumbnail}
		</button>
	)
}

/** The file name, a link to its detail when the card can open one. */
function CardName({ name, onOpen }: { name: string; onOpen?: () => void }) {
	if (!onOpen) return <span className="truncate text-sm text-ds-text">{name}</span>
	return (
		<button type="button" onClick={onOpen} className="truncate text-left text-sm text-ds-text hover:underline">
			{name}
		</button>
	)
}

/** Only a stored file has a detail to open. */
const openHandler = (fileId: string | null, upload: CardUploadState | undefined, onOpen?: (fileId: string) => void) =>
	fileId && !upload && onOpen ? () => onOpen(fileId) : undefined

/** The in-flight half of a card: byte progress, and how the transfer ended. */
export interface CardUploadState {
	progress: number
	isConfirming: boolean
	isConfirmed: boolean
	/** Pre-check rejection or a failed transfer — nothing was stored, so there is no retry. */
	rejectedReason?: string
}

export interface DeliverableCardProps {
	/** Null only before the upload URL is issued, which is the one moment no server id exists. */
	fileId: string | null
	name: string
	contentType?: string
	/**
	 * Presigned by the detail aggregate, absent until `thumbnail_generation`
	 * finishes.
	 *
	 * A prop rather than a query: the card used to fetch its own URL per file,
	 * which cost one request per image card AND keyed that query as
	 * `['file-thumbnail', id]` — a key the realtime bridge never invalidates (it
	 * only knows `['thumbnail', id]`), so a newly generated thumbnail never
	 * appeared from a push, only incidentally when the deliverables list refetched.
	 */
	thumbnailUrl?: string
	/** The local File, for an immediate preview before any server thumbnail exists. */
	file?: File
	/** Present while this card is an in-flight upload; absent once the server row owns it. */
	upload?: CardUploadState
	/** Per-concern state. Only failures render, as a warning badge. */
	concerns?: Record<string, ConcernState>
	onDownload: (fileId: string) => void
	/** Omitted for a reader who may not remove the file. */
	onDelete?: (fileId: string) => void
	onRetryConcern?: (concernId: string) => void
	/** Removes a rejected/failed upload row — there is nothing on the server to delete. */
	onDismiss?: () => void
	/** A drag handle (from a sortable wrapper) rendered before the thumbnail — visual boxes only. */
	dragHandle?: React.ReactNode
	/**
	 * Keep the drag handle's width even when this row has no handle.
	 *
	 * Set by a reorderable list so its uploading rows — which cannot be sortable
	 * yet — line up with the stored ones. Without it the thumbnail sits a handle's
	 * width to the left and everything jumps right the instant the upload settles.
	 */
	reservesDragSpace?: boolean
	/** Preview size — `lg` lines the card up beside a drop panel. */
	thumbnailSize?: ThumbnailSize
	/**
	 * Open this row's image at full size, called with the resolved source.
	 *
	 * Opt-in, and honoured only for a row that HAS an image (a real thumbnail or a
	 * local blob preview): a PSD or PDF row renders a format glyph, which there is
	 * nothing to enlarge.
	 */
	onPreview?: (src: string) => void
	/** Open the stored file's detail from its thumbnail or name; takes precedence over `onPreview`. */
	onOpen?: (fileId: string) => void
}

/**
 * One deliverable, for its whole life: uploading, stored, or stored-with-a-
 * failed-concern.
 *
 * Deliberately ONE component rather than an upload tile that hands over to a
 * card. Both used to render the same asset at different lifecycle points, keyed
 * differently, with a window where both or neither could show it and reflow as
 * one unmounted and the other mounted. Keying on `fileId` — known from
 * `requestUploadUrl`, before any byte is sent — makes the handover a data change
 * on a row that never moves.
 *
 * Presentational (prop-driven): the same card renders in the editable, visual,
 * and distribution boxes; the visual box passes a `dragHandle`. Storied.
 */
export function DeliverableCard({
	fileId,
	name,
	contentType,
	thumbnailUrl,
	file,
	upload,
	concerns = {},
	onDownload,
	onDelete,
	onRetryConcern,
	onDismiss,
	dragHandle,
	reservesDragSpace = false,
	thumbnailSize = 'sm',
	onPreview,
	onOpen,
}: DeliverableCardProps) {
	const localPreview = useFilePreview(file, contentType ?? '')
	// The server thumbnail once it exists, else the local blob while uploading.
	// Absent for a format that never gets one, which is what gates the preview.
	const previewSrc = thumbnailUrl ?? localPreview ?? undefined
	const isRejected = Boolean(upload?.rejectedReason)
	const open = openHandler(fileId, upload, onOpen)
	// The bar lives only while the transfer does. Once the server row owns this
	// card there is nothing left to report, and a completed bar sitting under
	// every stored file would be noise.
	const progress =
		upload && !isRejected
			? deriveUploadProgress({
					uploadProgress: upload.progress,
					isConfirming: upload.isConfirming,
					isConfirmed: upload.isConfirmed,
					concerns,
				})
			: null

	return (
		<li className={cn('flex items-center gap-3 rounded-ds-md border border-ds-border bg-ds-surface p-2')}>
			{dragHandle ?? (reservesDragSpace ? <DragHandlePlaceholder /> : null)}
			<CardThumbnail
				name={name}
				contentType={contentType}
				previewSrc={previewSrc}
				thumbnailSize={thumbnailSize}
				onPreview={onPreview}
				onOpen={open}
			/>
			<span className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
				<CardName name={name} onOpen={open} />
				{isRejected ? (
					<span className="text-ds-xs text-ds-danger">{upload?.rejectedReason}</span>
				) : progress ? (
					<span className="flex items-center gap-2">
						<ProgressBar size="sm" value={progress.percent} aria-label={`Upload progress for ${name}`} />
						<span className="shrink-0 text-ds-xs text-ds-text-muted">{progress.label}</span>
					</span>
				) : (
					<ConcernWarning concerns={concerns} onRetryConcern={onRetryConcern} />
				)}
			</span>
			<span className="flex shrink-0 items-center gap-1">
				{/*
				 * Download and delete need a stored file. A row still transferring —
				 * or one rejected before any byte was sent — offers dismissal instead,
				 * so the affordances never promise something the server cannot do.
				 */}
				{fileId && !upload ? (
					<>
						<button
							type="button"
							aria-label={`Download ${name}`}
							onClick={() => onDownload(fileId)}
							className="rounded-ds-sm p-1 text-ds-text-subtle hover:bg-ds-surface-elevated hover:text-ds-text"
						>
							<Download className="h-4 w-4" />
						</button>
						{onDelete ? (
							<button
								type="button"
								aria-label={`Delete ${name}`}
								onClick={() => onDelete(fileId)}
								className="rounded-ds-sm p-1 text-ds-text-subtle hover:bg-ds-surface-elevated hover:text-ds-danger-strong"
							>
								<Trash2 className="h-4 w-4" />
							</button>
						) : null}
					</>
				) : (
					onDismiss && (
						<button
							type="button"
							aria-label={`Dismiss ${name}`}
							onClick={onDismiss}
							className="rounded-ds-sm p-1 text-ds-text-subtle hover:bg-ds-surface-elevated hover:text-ds-text"
						>
							<X className="h-4 w-4" />
						</button>
					)
				)}
			</span>
		</li>
	)
}

/** A grab-cursor drag handle icon for sortable cards (spread the sortable listeners onto it). */
export function DragHandle(props: React.HTMLAttributes<HTMLButtonElement>) {
	return (
		<button
			type="button"
			{...props}
			aria-label="Reorder"
			className="cursor-grab touch-none rounded-ds-sm p-1 text-ds-text-subtle hover:text-ds-text active:cursor-grabbing"
		>
			<GripVertical className="h-4 w-4" />
		</button>
	)
}

/**
 * The handle's footprint, with nothing in it.
 *
 * An uploading row is not sortable — it has no persisted order to write — but in
 * a reorderable list it must still occupy the handle's width, or the thumbnail
 * and everything after it shifts sideways the moment the row becomes draggable.
 * Matches {@link DragHandle}'s box exactly: `p-1` around a `h-4 w-4` glyph.
 */
function DragHandlePlaceholder() {
	return (
		<span aria-hidden="true" className="p-1">
			<span className="block h-4 w-4" />
		</span>
	)
}
